import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Search,
  Close,
  RecordVoiceOver,
  SmartToyOutlined,
  DescriptionOutlined,
  FolderOpen,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Mic,
  ChevronRight,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { formatDate } from '../utils/dateUtils';
import { goToRecordMeeting, projectPath, sessionPath } from '../utils/navigation';
import { PageContainer, PageHeader, SectionCard, EmptyState, ListSkeleton, IconTile } from './pagekit';
import { parseExtractionTypeLabels } from './sessions/transcriptUtils';

const SOURCE_FILTERS = [
  { value: 'all', label: 'All sources' },
  { value: 'fathom', label: 'Fathom' },
  { value: 'recall', label: 'Notetaker' },
  { value: 'manual', label: 'Uploads' },
];

/**
 * Icon for a session's source.
 * @param {object} props
 * @param {string} props.source - Session source identifier.
 * @returns {JSX.Element} Icon element.
 */
function SourceIcon({ source }) {
  switch (source) {
    case 'fathom':
      return <RecordVoiceOver />;
    case 'recall':
      return <SmartToyOutlined />;
    default:
      return <DescriptionOutlined />;
  }
}

/**
 * All Sessions page: every session across projects, grouped by project,
 * searchable by session, description, project and customer name.
 * @returns {JSX.Element} All Sessions page.
 */
function AllSessionsPage() {
  const navigate = useNavigate();
  const { state } = useApp();
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [collapsed, setCollapsed] = useState(() => new Set());

  /** All sessions with their project and customer. */
  const enrichedRows = useMemo(() => {
    const rows = [];
    for (const project of state.projects || []) {
      const customer = (state.customers || []).find((c) => c.id === project.customer_id) || project.customer || null;
      for (const session of project.sessions || []) {
        const requirementCount = (project.requirements || []).filter((r) => r.sessionId === session.id).length;
        rows.push({ session, project, customer, requirementCount });
      }
    }
    return rows;
  }, [state.projects, state.customers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enrichedRows.filter(({ session, project, customer }) => {
      const source = session.source || 'manual';
      if (sourceFilter !== 'all' && source !== sourceFilter) return false;
      if (!q) return true;
      return (
        session.name?.toLowerCase().includes(q) ||
        session.description?.toLowerCase().includes(q) ||
        project.name?.toLowerCase().includes(q) ||
        customer?.name?.toLowerCase().includes(q)
      );
    });
  }, [enrichedRows, search, sourceFilter]);

  /** Filtered rows grouped by project id, in project order. */
  const groupedByProject = useMemo(() => {
    const map = new Map();
    for (const row of filtered) {
      if (!map.has(row.project.id)) {
        map.set(row.project.id, { project: row.project, customer: row.customer, rows: [] });
      }
      map.get(row.project.id).rows.push(row);
    }
    return [...map.values()];
  }, [filtered]);

  const totalSessions = enrichedRows.length;
  const projectCount = (state.projects || []).length;

  const toggleGroup = useCallback((projectId) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  }, []);

  const loading = state.loading || !state.initialLoadComplete;

  return (
    <PageContainer>
      <PageHeader
        title="Sessions"
        description={loading
          ? 'Meetings, transcripts and files across your projects.'
          : `${totalSessions} session${totalSessions !== 1 ? 's' : ''} across ${projectCount} project${projectCount !== 1 ? 's' : ''}. Open one to see its transcript, summary and requirements.`}
        actions={
          <Button variant="contained" startIcon={<Mic />} onClick={() => goToRecordMeeting(navigate)}>
            Record a meeting
          </Button>
        }
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search sessions, projects or customers"
          inputProps={{ 'aria-label': 'Search sessions' }}
          sx={{ flex: '1 1 280px', maxWidth: 480, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => setSearch('')}>
                  <Close fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Box role="group" aria-label="Filter by source" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {SOURCE_FILTERS.map((f) => (
            <Chip
              key={f.value}
              label={f.label}
              clickable
              color={sourceFilter === f.value ? 'primary' : 'default'}
              variant={sourceFilter === f.value ? 'filled' : 'outlined'}
              aria-pressed={sourceFilter === f.value}
              onClick={() => setSourceFilter(f.value)}
            />
          ))}
        </Box>
      </Box>

      {loading ? (
        <SectionCard flush>
          <ListSkeleton rows={6} label="Loading sessions" />
        </SectionCard>
      ) : groupedByProject.length === 0 ? (
        <SectionCard>
          {totalSessions === 0 ? (
            <EmptyState
              icon={<Mic />}
              title="No sessions yet"
              description="Record a meeting from Home, or open a project and upload a transcript or notes."
              actions={
                <>
                  <Button variant="contained" startIcon={<Mic />} onClick={() => goToRecordMeeting(navigate)}>
                    Record a meeting
                  </Button>
                  <Button variant="outlined" onClick={() => navigate('/projects')}>Open a project</Button>
                </>
              }
            />
          ) : (
            <EmptyState
              compact
              icon={<Search />}
              title="No sessions match"
              description="Try a different search or source."
              actions={<Button variant="outlined" onClick={() => { setSearch(''); setSourceFilter('all'); }}>Clear filters</Button>}
            />
          )}
        </SectionCard>
      ) : (
        groupedByProject.map(({ project, customer, rows }) => {
          const isCollapsed = collapsed.has(project.id);
          return (
            <SectionCard key={project.id} flush component="section" aria-label={project.name}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                  px: { xs: 1.5, sm: 2 },
                  py: 1.25,
                  borderBottom: isCollapsed ? 0 : 1,
                  borderColor: 'var(--q-border-subtle)',
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => toggleGroup(project.id)}
                  aria-expanded={!isCollapsed}
                  aria-label={isCollapsed ? `Show sessions of ${project.name}` : `Hide sessions of ${project.name}`}
                >
                  {isCollapsed ? <KeyboardArrowRight fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                </IconButton>
                <FolderOpen fontSize="small" sx={{ color: 'var(--q-accent-text)' }} />
                <Typography variant="subtitle2" component="h2" sx={{ minWidth: 0 }} noWrap>
                  {project.name}
                </Typography>
                {customer && <Chip label={customer.name} size="small" variant="outlined" />}
                <Chip label={`${rows.length} session${rows.length !== 1 ? 's' : ''}`} size="small" />
                <Box sx={{ flex: 1 }} />
                <Button size="small" onClick={() => navigate(projectPath(project.id, 'sessions'))}>
                  Open project
                </Button>
              </Box>
              {!isCollapsed && (
                <Box component="ul" sx={{ m: 0, p: 0 }}>
                  {rows.map(({ session, requirementCount }) => {
                    const labels = parseExtractionTypeLabels(session.extractionType);
                    return (
                      <Box
                        component="li"
                        key={session.id}
                        sx={{ listStyle: 'none', borderBottom: 1, borderColor: 'var(--q-row-divider)', '&:last-of-type': { borderBottom: 0 } }}
                      >
                        <Box
                          component="button"
                          type="button"
                          onClick={() => navigate(sessionPath(session.id))}
                          sx={{
                            all: 'unset',
                            boxSizing: 'border-box',
                            width: '100%',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: { xs: 1.5, sm: 2 },
                            py: 1.25,
                            '&:hover': { bgcolor: 'var(--q-bg-subtle)' },
                            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
                          }}
                        >
                          <IconTile tone={session.source === 'fathom' || session.source === 'recall' ? 'accent' : 'neutral'} size={32}>
                            <SourceIcon source={session.source} />
                          </IconTile>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                              {session.name || 'Untitled session'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap component="div">
                              {[session.createdAt ? formatDate(session.createdAt) : null, session.description].filter(Boolean).join(' · ')}
                            </Typography>
                          </Box>
                          <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.75, alignItems: 'center' }}>
                            {labels.length > 0 && (
                              <Tooltip title={labels.join(', ')}>
                                <Chip label={labels[0]} size="small" variant="outlined" />
                              </Tooltip>
                            )}
                            <Chip
                              size="small"
                              color={requirementCount ? 'success' : 'warning'}
                              label={requirementCount ? `${requirementCount} requirement${requirementCount !== 1 ? 's' : ''}` : 'Not extracted'}
                            />
                          </Box>
                          <ChevronRight fontSize="small" sx={{ color: 'text.secondary' }} />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </SectionCard>
          );
        })
      )}
    </PageContainer>
  );
}

export default AllSessionsPage;
